import { useState, useEffect } from 'react';
import {
  Calendar,
} from 'react-big-calendar';
import { useParams, useNavigate } from 'react-router';
import {
  DateTime,
} from 'luxon';

import {
  WorkoutEvent,
} from '@gymcloud/core'

import {
  Tooltip,
  ActivityModal,
  WorkoutResultModal,
} from 'web-components';

import Toolbar from './Toolbar';
import LuxonLocalizer from './luxonLocalizer';

import * as S from './styled';

interface EventProps {
  event: {
    color: string;
    isLog: boolean;
    startTime: string;
    person_name: string;
  }
}

interface ComponentsProps {
  events: WorkoutEvent[]
  clients: any
  calendarSelectedUser: any
  isTooltipVisible: boolean,
  onFilter: { (userId: number): void }
  onNavigate: { (day: Date): void }
  onNewWorkoutClick: { ({ start }: { start: Date }): void }
}

const getComponents = ({
  events,
  clients,
  calendarSelectedUser,
  isTooltipVisible,
  onFilter,
  onNewWorkoutClick,
}: ComponentsProps) => ({
  toolbar: (props: any) => (
    <Toolbar
      {...props}
      onNewWorkoutClick={() => onNewWorkoutClick({ start: new Date() })}
      clients={clients}
      onFilter={onFilter}
      calendarSelectedUser={calendarSelectedUser}
    />
  ),
  event: ({ event }: EventProps) => (
    <S.Event color={event.color}>
      {event.startTime}
      {' '}
      {event.person_name}
    </S.Event>
  ),
  dateCellWrapper: ({ value, children }: { value: Date, children: any }) => {
    if (!isTooltipVisible) return children

    const last = events[events.length - 1]
    if (!last?.start) return children

    const dt = DateTime.fromJSDate(last?.start || new Date());
    const targetDate = DateTime.fromJSDate(value)

    return dt.hasSame(targetDate, 'day') ? (
      <Tooltip
        body="Click on a workout to view the results."
        isOpen={isTooltipVisible}
      >
        {children}
      </Tooltip>
    ) : children
  }
});

interface ContainerProps extends ComponentsProps {
  loaded: boolean
  events: Array<any>
  defaultDate?: any
  onNavigate: { (day: Date): void }
  onNewWorkoutClick: { ({ start }: { start: Date }): void }
}

const CalendarContainer = (props: ContainerProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const {
    loaded,
    events,
    onNavigate,
    onNewWorkoutClick,
    defaultDate,
  } = props;
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  useEffect(() => {
    if (params.id && loaded && events.length > 0) {
      const event = events.find(e => e.id === Number(params.id));
      if (event && event.isLog) {
        setSelectedLog(event);
      } else if (event && !event.isLog) {
        setSelectedEvent(event);
      }
      navigate('/calendar', { replace: true });
    }
  }, [params.id, loaded, events]);

  return (
    <S.CalendarContainer>
      <Calendar
        style={{
          width: '100%',
        }}
        views={['month']}
        defaultDate={defaultDate}
        popup
        events={events}
        selectable
        localizer={LuxonLocalizer({ firstDayOfWeek: 0 })}
        components={getComponents(props)}
        onSelectEvent={(event) => {
          if (event.isLog) setSelectedLog(event);
          if (!event.isLog) setSelectedEvent(event);
        }}
        onNavigate={onNavigate}
        onSelectSlot={onNewWorkoutClick}
      />
      <ActivityModal
        id={selectedLog?.id}
        onRequestClose={() => setSelectedLog(null)}
      />
      <WorkoutResultModal
        id={selectedEvent?.id}
        onRequestClose={() => setSelectedEvent(null)}
      />
    </S.CalendarContainer>
  );
};

export default CalendarContainer;
